import { ControlType } from '@/common/components/business/DynamicComponent/config';

export interface AreaProtectionApprovalInfo {
  applicationType: string;
  submittedAt: string;
  chancePointName: string;
  city: string;
  address: string;
  applicant: string;
  applicationReason: string;
  dangerConflictCount: number;
  conflicts: Array<{
    id: string;
    name: string;
    type: string;
    walkingDistance: string;
    status: string;
    protectionEndDate: string;
    detailRows: Array<{
      label: string;
      value: string;
    }>;
  }>;
}

const parseTextValue = (value: any) => {
  if (typeof value !== 'string') return value || {};
  try {
    return JSON.parse(value);
  } catch (error) {
    console.log('解析防撞店审批详情动态字段失败', error);
    return {};
  }
};

const findDynamicControl = (components: any[], controlType: number): any => {
  if (!Array.isArray(components)) return undefined;

  for (const component of components) {
    if (component?.controlType === controlType) return component;

    const fromSubForm = findDynamicControl(component?.formConfigList, controlType);
    if (fromSubForm) return fromSubForm;

    const childList = Array.isArray(component?.childList) ? component.childList : [];
    for (const child of childList) {
      const fromChild = findDynamicControl(child?.propertyConfigVOList, controlType);
      if (fromChild) return fromChild;
    }
  }

  return undefined;
};

const getPropertyGroupVOList = (chancePointDetail: any) => {
  const moduleDetail = Array.isArray(chancePointDetail?.moduleDetails)
    ? chancePointDetail.moduleDetails.find((item: any) => Array.isArray(item?.infoModule?.propertyGroupVOList))
    : undefined;
  return moduleDetail?.infoModule?.propertyGroupVOList || [];
};

const formatDistance = (distance?: number) => {
  if (typeof distance !== 'number') return '-';
  if (distance < 1000) return `${distance}m`;
  return `${Number((distance / 1000).toFixed(1))}km`;
};

const getConflictDetailRows = (item: any) => {
  if (item?.type === '门店') {
    return [
      { label: '门店编码', value: item?.code || item?.storeCode || item?.number || '-' },
      { label: '所属加盟商', value: item?.franchiseeName || item?.franchisee || '-' },
      { label: '开业时间', value: item?.openDate || item?.openingDate || item?.openedAt || '-' },
      { label: '保护期', value: item?.protectionExpiry || '-' },
      { label: '详细地址', value: item?.address || '-' },
    ];
  }

  return [
    { label: '所属加盟商', value: item?.franchiseeName || item?.franchisee || '-' },
    { label: '拓展人员', value: item?.expansionPerson || item?.developer || item?.expander || '-' },
    { label: '创建时间', value: item?.createdAt || item?.createTime || '-' },
    { label: '保护期', value: item?.protectionExpiry || '-' },
    { label: '关联任务', value: item?.taskName || item?.relationTaskName || '-' },
  ];
};

/** 从机会点详情和审批详情组装防撞店特殊申请展示数据。 */
export const getAreaProtectionApprovalInfo = (chancePointDetail: any, approvalDetail: any): AreaProtectionApprovalInfo => {
  const propertyGroupVOList = getPropertyGroupVOList(chancePointDetail);
  const addressValue = parseTextValue(findDynamicControl(propertyGroupVOList, ControlType.ADDRESS.value)?.textValue);
  const areaValue = parseTextValue(findDynamicControl(propertyGroupVOList, ControlType.AREA.value)?.textValue);
  const areaProtectionValue = parseTextValue(findDynamicControl(propertyGroupVOList, ControlType.AREA_PROTECTION.value)?.textValue);
  const cityParts = [
    chancePointDetail?.provinceName || areaValue?.provinceName,
    chancePointDetail?.cityName || areaValue?.cityName,
    chancePointDetail?.districtName || areaValue?.districtName,
  ].filter((item, index, values) => !!item && values.indexOf(item) === index);
  const collisionGroups = Array.isArray(areaProtectionValue?.groups) ? areaProtectionValue.groups : [];
  const dangerConflictCount = collisionGroups
    .filter((group: any) => group?.range === '200m内')
    .reduce((count: number, group: any) => count + (Array.isArray(group?.items) ? group.items.length : 0), 0);
  const conflicts = collisionGroups
    .filter((group: any) => group?.range === '200m内' || group?.range === '200m-500m')
    .reduce((items: any[], group: any) => items.concat(Array.isArray(group?.items) ? group.items : []), [])
    .map((item: any, index: number) => ({
      id: `${item?.name || 'collision'}-${item?.distance || index}-${index}`,
      name: item?.name || '-',
      type: item?.type || '-',
      walkingDistance: formatDistance(item?.distance),
      status: item?.status || '-',
      protectionEndDate: item?.protectionExpiry || '-',
      detailRows: getConflictDetailRows(item),
    }));

  return {
    applicationType: '突破区域保护(撞店特批)',
    submittedAt: approvalDetail?.createdAt || '-',
    chancePointName: chancePointDetail?.name || '-',
    city: cityParts.join('/') || '-',
    address: chancePointDetail?.address || addressValue?.address || addressValue?.poiName || '-',
    applicant: approvalDetail?.creator || '-',
    applicationReason: approvalDetail?.remark || '-',
    dangerConflictCount,
    conflicts,
  };
};
